// Provider settlement reports (slice 51a, ADR 0046; endpoints from ADR 0045).
//
// The owner pastes or picks the provider's report in the normalised JSON
// shape; the server checks the report's own arithmetic and matches each line
// with OUR records. A mismatch is shown, never corrected. Re-importing the
// same report is safe (the server answers "already imported").

import { useRef, useState, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from '../api/client.js';
import type { MeResponse } from '../api/types.js';
import type {
  PosSettlementImport,
  PosSettlementLine,
  SettlementMatchState,
} from '../api/pos-types.js';
import { hasPermission, useOrgPermissions } from '../auth/permissions.js';
import { Chip, QueryState } from './pos/common.js';
import { formatDateTime, formatRupees } from './pos/format.js';
import { MATCH_TEXT, textOr } from './pos/labels.js';

const MATCH_TONE: Record<SettlementMatchState, 'ok' | 'warn' | 'bad'> = {
  matched: 'ok',
  amount_mismatch: 'bad',
  not_succeeded: 'bad',
  unmatched: 'warn',
};

const EXAMPLE = `{
  "provider": "simulated",
  "settlementRef": "SETTLE-2026-10-10",
  "settledAt": "2026-10-11T04:00:00.000Z",
  "lines": [
    {
      "kind": "payment",
      "providerPaymentId": "pay_123",
      "grossPaise": 25000,
      "feePaise": 500,
      "feeTaxPaise": 90,
      "netPaise": 24410
    }
  ]
}`;

/** Parses what the owner pasted; returns the object or a sentence saying what is wrong. */
export function parseSettlementText(
  text: string,
): { ok: true; body: unknown } | { ok: false; error: string } {
  const trimmed = text.trim();
  if (!trimmed) return { ok: false, error: 'Paste or choose the settlement report first.' };
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { ok: false, error: 'That is not valid JSON. Check that the whole file was pasted.' };
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { ok: false, error: 'The report must be one JSON object.' };
  }
  const o = parsed as Record<string, unknown>;
  if (typeof o['settlementRef'] !== 'string' || !Array.isArray(o['lines'])) {
    return { ok: false, error: 'The report needs a settlementRef and a list of lines.' };
  }
  return { ok: true, body: parsed };
}

function importError(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    const code = (err.body as { code?: string } | undefined)?.code;
    if (err.status === 403) return 'You do not have permission to import settlement reports.';
    if (code === 'settlement_conflict')
      return 'A report with this reference was already imported with different contents. Nothing was changed.';
    if (code === 'settlement_line_invalid')
      return 'The report’s own arithmetic does not hold on some lines, so it was not imported. Ask the provider for a corrected report.';
    if (code === 'settlement_duplicate_line')
      return 'The report lists the same payment or refund twice, so it was not imported.';
    if (err.status === 400) return `The report was not accepted: ${err.message}`;
    if (err.status === 0) return 'Network error — check your connection.';
  }
  return 'Could not import the report. Try again.';
}

export function PosSettlementsPage(): JSX.Element {
  const { orgId, restaurantId } = useParams<{ orgId: string; restaurantId: string }>();
  const qc = useQueryClient();
  // Populates the ['me'] cache the permission hooks read, on a deep link too.
  useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<MeResponse>('/me'),
    staleTime: 30_000,
  });
  const canManage = hasPermission(useOrgPermissions(orgId), 'integration:manage');
  const [text, setText] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const base = `/organizations/${orgId}/restaurants/${restaurantId}/pos/settlements`;

  const list = useQuery({
    queryKey: ['pos-settlements', orgId, restaurantId],
    queryFn: () => apiFetch<{ settlements: PosSettlementImport[] }>(base),
    enabled: !!orgId && !!restaurantId,
  });
  const detail = useQuery({
    queryKey: ['pos-settlement', orgId, restaurantId, selected],
    queryFn: () =>
      apiFetch<PosSettlementImport & { lines: PosSettlementLine[] }>(`${base}/${selected}`),
    enabled: !!selected,
  });

  const submit = useMutation({
    mutationFn: (body: unknown) => apiFetch<PosSettlementImport>(base, { method: 'POST', body }),
    onSuccess: (imported) => {
      setText('');
      setSelected(imported.id);
      void qc.invalidateQueries({ queryKey: ['pos-settlements', orgId, restaurantId] });
    },
  });

  async function chooseFile(file: File | undefined): Promise<void> {
    if (!file) return;
    if (file.size > 2_000_000) {
      setParseError(
        'That file is larger than 2 MB, which is more than a settlement report should be.',
      );
      return;
    }
    setParseError(null);
    setText(await file.text());
  }

  function onImport(): void {
    const parsed = parseSettlementText(text);
    if (!parsed.ok) {
      setParseError(parsed.error);
      return;
    }
    setParseError(null);
    submit.mutate(parsed.body);
  }

  const result = submit.data;
  const error = parseError ?? importError(submit.error);

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>Settlement reports</h1>
          <p className="muted">
            <Link to="/home">Your outlets</Link>
          </p>
        </div>
      </header>

      {canManage ? (
        <section className="card" aria-labelledby="import-heading">
          <h2 id="import-heading">Import a report</h2>
          <p className="muted">
            Each line is compared with what we recorded. Differences are shown below and raised to
            you; nothing in our records is changed by an import.
          </p>
          <div className="row-actions">
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              aria-label="Settlement report file"
              onChange={(e) => void chooseFile(e.target.files?.[0])}
            />
          </div>
          <label className="block-field">
            Report (JSON)
            <textarea
              rows={8}
              value={text}
              placeholder={EXAMPLE}
              onChange={(e) => setText(e.target.value)}
              spellCheck={false}
            />
          </label>
          {error ? (
            <p className="error" role="alert">
              {error}
            </p>
          ) : null}
          {result ? (
            <p role="status">
              {result.replay ? 'This report was already imported; nothing changed. ' : 'Imported. '}
              {result.lineCount} {result.lineCount === 1 ? 'line' : 'lines'}, {result.matchedCount}{' '}
              matched, {result.exceptionCount} {result.exceptionCount === 1 ? 'differs' : 'differ'}.
            </p>
          ) : null}
          <button type="button" disabled={submit.isPending} onClick={onImport}>
            {submit.isPending ? 'Importing…' : 'Import report'}
          </button>
        </section>
      ) : (
        <p className="muted">Only an owner can import a report. You can read the ones imported.</p>
      )}

      <h2>Imported reports</h2>
      <QueryState loading={list.isLoading} error={list.error} />
      {list.data && list.data.settlements.length === 0 ? (
        <p className="muted">No reports have been imported yet.</p>
      ) : null}
      {list.data && list.data.settlements.length > 0 ? (
        <div className="table-wrap">
          <table className="pos-table">
            <thead>
              <tr>
                <th scope="col">Reference</th>
                <th scope="col">Settled</th>
                <th scope="col">Lines</th>
                <th scope="col">Differ</th>
                <th scope="col">Gross</th>
                <th scope="col">Fees</th>
                <th scope="col">Net</th>
              </tr>
            </thead>
            <tbody>
              {list.data.settlements.map((s) => (
                <tr key={s.id} className={selected === s.id ? 'row-selected' : undefined}>
                  <th scope="row">
                    <button type="button" className="link-button" onClick={() => setSelected(s.id)}>
                      {s.settlementRef}
                    </button>
                    <div className="muted">{s.provider}</div>
                  </th>
                  <td>{formatDateTime(s.settledAt)}</td>
                  <td>{s.lineCount}</td>
                  <td>
                    {s.exceptionCount > 0 ? (
                      <Chip tone="bad">{String(s.exceptionCount)}</Chip>
                    ) : (
                      '0'
                    )}
                  </td>
                  <td>{formatRupees(s.grossPaise)}</td>
                  <td>{formatRupees(s.feePaise)}</td>
                  <td>{formatRupees(s.netPaise)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {selected ? (
        <section aria-labelledby="lines-heading">
          <h2 id="lines-heading">Lines</h2>
          <QueryState loading={detail.isLoading} error={detail.error} />
          {detail.data ? (
            <div className="table-wrap">
              <table className="pos-table">
                <thead>
                  <tr>
                    <th scope="col">#</th>
                    <th scope="col">Kind</th>
                    <th scope="col">Provider id</th>
                    <th scope="col">Amount</th>
                    <th scope="col">Against our records</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data.lines.map((l) => (
                    <tr key={l.lineNo}>
                      <th scope="row">{l.lineNo}</th>
                      <td>{l.kind === 'payment' ? 'Payment' : 'Refund'}</td>
                      <td>
                        <code>{l.providerPaymentId ?? l.providerRefundId}</code>
                      </td>
                      <td>{formatRupees(l.grossPaise)}</td>
                      <td>
                        <Chip tone={MATCH_TONE[l.matchState]}>
                          {textOr(MATCH_TEXT, l.matchState)}
                        </Chip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}
    </main>
  );
}
